/**
 * Doc organization — nested folders for the Docs library / Atlas.
 *
 * PERSISTENCE TRICK: the /sops lambda whitelists doc fields, so organization
 * data can't live as new fields on each doc without a backend deploy.
 * Instead, ALL organization state (folder list + doc→folder mapping) lives
 * in ONE reserved doc record whose `body` holds a single payload object
 * instead of editor blocks. It rides the existing create/update endpoints,
 * syncs across devices/users like any doc, and is hidden from every list
 * surface via `isDocsMetaDoc`.
 *
 * Folders may nest via `parentId` (null = root). Docs are always leaves —
 * a document never contains other documents.
 */

/** Reserved id of the hidden organization record (client-chosen — POST /sops honors `id`). */
export const DOC_ORG_ID = 'doc-organization-meta';

/** Reserved tag marking system records that must never surface as real docs. */
export const DOCS_META_TAG = 'docs-meta';

export const isDocsMetaDoc = (doc) =>
  doc?.id === DOC_ORG_ID ||
  (Array.isArray(doc?.tags) && doc.tags.includes(DOCS_META_TAG));

const ORG_BODY_TYPE = 'doc-organization';

export const createEmptyOrganization = () => ({
  /** [{ id, name, parentId }] in display order among siblings */
  folders: [],
  /** { [docId]: folderId } — a doc lives in at most one folder */
  docFolders: {},
});

export const generateFolderId = () =>
  `folder-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** Normalize a folder row; missing parentId → root. */
export const normalizeFolder = (f) => {
  if (!f || !f.id || typeof f.name !== 'string') return null;
  return {
    id: f.id,
    name: f.name,
    parentId: f.parentId || null,
  };
};

/** Read the organization payload out of the meta doc; tolerant of anything malformed. */
export const parseOrganization = (doc) => {
  const raw = Array.isArray(doc?.body)
    ? doc.body.find((entry) => entry && entry.type === ORG_BODY_TYPE)
    : null;
  if (!raw) return createEmptyOrganization();
  const folders = Array.isArray(raw.folders)
    ? raw.folders.map(normalizeFolder).filter(Boolean)
    : [];
  return {
    folders,
    docFolders:
      raw.docFolders && typeof raw.docFolders === 'object' && !Array.isArray(raw.docFolders)
        ? { ...raw.docFolders }
        : {},
  };
};

/** Serialize the organization into the meta doc's `body` array. */
export const serializeOrganization = (org) => [
  {
    type: ORG_BODY_TYPE,
    version: 2,
    folders: org.folders,
    docFolders: org.docFolders,
  },
];

/** Direct children of a folder (or root when parentId is null), in list order. */
export const getChildFolders = (folders, parentId = null) =>
  (folders || []).filter((f) => (f.parentId || null) === (parentId || null));

/** Reparent a folder and insert it before/after a sibling (array order = display order). */
export const placeFolderAmongSiblings = (
  folders,
  folderId,
  { parentId = null, beforeId = null, afterId = null } = {}
) => {
  const list = folders || [];
  const folder = list.find((f) => f.id === folderId);
  if (!folder) return list;
  const moved = { ...folder, parentId: parentId || null };
  const without = list.filter((f) => f.id !== folderId);
  let insertAt = without.length;
  if (beforeId) {
    const i = without.findIndex((f) => f.id === beforeId);
    if (i >= 0) insertAt = i;
  } else if (afterId) {
    const i = without.findIndex((f) => f.id === afterId);
    if (i >= 0) insertAt = i + 1;
  }
  const next = [...without];
  next.splice(insertAt, 0, moved);
  return next;
};

export const foldersOrderEqual = (a, b) =>
  (a || []).length === (b || []).length &&
  (a || []).every(
    (f, i) =>
      f.id === b[i].id && (f.parentId || null) === (b[i].parentId || null)
  );

/** True if `maybeAncestorId` is the folder itself or an ancestor of `folderId`. */
export const isAncestorFolder = (folders, folderId, maybeAncestorId) => {
  if (!folderId || !maybeAncestorId) return false;
  if (folderId === maybeAncestorId) return true;
  const byId = new Map((folders || []).map((f) => [f.id, f]));
  let current = byId.get(folderId);
  const guard = new Set();
  while (current) {
    if (guard.has(current.id)) break;
    guard.add(current.id);
    const pid = current.parentId || null;
    if (!pid) return false;
    if (pid === maybeAncestorId) return true;
    current = byId.get(pid);
  }
  return false;
};

/** Breadcrumb path from root to folder (inclusive), or [] if missing. */
export const getFolderPath = (folders, folderId) => {
  if (!folderId) return [];
  const byId = new Map((folders || []).map((f) => [f.id, f]));
  const path = [];
  let current = byId.get(folderId);
  const guard = new Set();
  while (current && !guard.has(current.id)) {
    guard.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : null;
  }
  return path;
};

/**
 * Collect folder id + all descendant folder ids.
 * Used when deleting a folder (cascade) or counting nested docs.
 */
export const collectFolderSubtreeIds = (folders, rootId) => {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders || []) {
      if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) {
        ids.add(f.id);
        grew = true;
      }
    }
  }
  return ids;
};

/** Selected folders that do not have an ancestor also in the set. */
export const topmostFolderIds = (folders, folderIds) => {
  const requested = [...new Set((folderIds || []).filter(Boolean))];
  return requested.filter(
    (id) =>
      !requested.some((other) => other !== id && isAncestorFolder(folders, id, other))
  );
};

const destinationLabelFor = (folders, parentIds) => {
  const unique = [...new Set(parentIds)];
  if (unique.length !== 1) return 'their parent folders';
  const parentId = unique[0];
  if (!parentId) return 'your library';
  const parent = (folders || []).find((f) => f.id === parentId);
  return parent?.name ? `"${parent.name}"` : 'the parent folder';
};

/**
 * Counts and copy for the delete-folder confirm modal.
 * `canSkipConfirm` is true when the subtree has no docs and no nested folders.
 */
export const describeFolderDelete = (folders, docFolders, folderIds) => {
  const list = folders || [];
  const byId = new Map(list.map((f) => [f.id, f]));
  const requested = [...new Set((folderIds || []).filter((id) => byId.has(id)))];
  const topmost = topmostFolderIds(list, requested);

  const cascadeRemove = new Set();
  for (const id of topmost) {
    for (const sid of collectFolderSubtreeIds(list, id)) cascadeRemove.add(sid);
  }
  const cascadeSubfolderCount = Math.max(0, cascadeRemove.size - topmost.length);
  const cascadeDocCount = Object.values(docFolders || {}).filter((fid) =>
    cascadeRemove.has(fid)
  ).length;

  const dissolveRemove = new Set(requested);
  const dissolveChildFolderCount = list.filter(
    (f) => dissolveRemove.has(f.parentId) && !dissolveRemove.has(f.id)
  ).length;
  const dissolveDocCount = Object.values(docFolders || {}).filter((fid) =>
    dissolveRemove.has(fid)
  ).length;

  return {
    folderIds: requested,
    topmostIds: topmost,
    names: topmost.map((id) => byId.get(id)?.name).filter(Boolean),
    destinationLabel: destinationLabelFor(
      list,
      topmost.map((id) => byId.get(id)?.parentId || null)
    ),
    cascadeSubfolderCount,
    cascadeDocCount,
    dissolveChildFolderCount,
    dissolveDocCount,
    canSkipConfirm: cascadeSubfolderCount === 0 && cascadeDocCount === 0,
  };
};

const refileDocs = (docFolders, fromIds, destParentId) => {
  const next = { ...docFolders };
  for (const [docId, fid] of Object.entries(next)) {
    if (!fromIds.has(fid)) continue;
    if (destParentId) next[docId] = destParentId;
    else delete next[docId];
  }
  return next;
};

/**
 * Delete folders. Docs are never deleted.
 * cascadeSubfolders true (default): remove the whole branch; docs move to the leftover parent.
 * false: remove only the selected folders; their subfolders and docs move up.
 */
export const applyDeleteFolders = (org, folderIds, { cascadeSubfolders = true } = {}) => {
  const folders = org?.folders || [];
  const docFolders = org?.docFolders || {};
  const byId = new Map(folders.map((f) => [f.id, f]));
  const requested = [...new Set((folderIds || []).filter((id) => byId.has(id)))];
  if (requested.length === 0) return org;

  if (cascadeSubfolders) {
    const topmost = topmostFolderIds(folders, requested);
    const remove = new Set();
    const destByFolder = new Map();
    for (const id of topmost) {
      const dest = byId.get(id)?.parentId || null;
      for (const sid of collectFolderSubtreeIds(folders, id)) {
        remove.add(sid);
        destByFolder.set(sid, dest);
      }
    }
    const nextDocs = { ...docFolders };
    for (const [docId, fid] of Object.entries(nextDocs)) {
      if (!remove.has(fid)) continue;
      const dest = destByFolder.get(fid) || null;
      if (dest) nextDocs[docId] = dest;
      else delete nextDocs[docId];
    }
    return {
      ...org,
      folders: folders.filter((f) => !remove.has(f.id)),
      docFolders: nextDocs,
    };
  }

  const depthOf = (id) => getFolderPath(folders, id).length;
  const ordered = [...requested].sort((a, b) => depthOf(b) - depthOf(a));
  let nextFolders = folders;
  let nextDocs = docFolders;
  for (const id of ordered) {
    const folder = nextFolders.find((f) => f.id === id);
    if (!folder) continue;
    const dest = folder.parentId || null;
    nextFolders = nextFolders
      .filter((f) => f.id !== id)
      .map((f) => (f.parentId === id ? { ...f, parentId: dest } : f));
    nextDocs = refileDocs(nextDocs, new Set([id]), dest);
  }
  return { ...org, folders: nextFolders, docFolders: nextDocs };
};
